import {
  CheckCircleOutlined,
  DownloadOutlined,
  HistoryOutlined,
} from "@ant-design/icons";
import { Alert, Button, Divider, Space as AntdSpace, Tag, Tooltip } from "antd";
import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import AppContext from "../../../context/app/context";
import ScaleContext from "../../../context/scale/context";
import ScaleContextProvider from "../../../context/scale/provider";
import PageHeader from "../../general/layout/page-header";
import RefreshBtn from "../../general/table-components/actions/refresh-btn";
import Can from "../../../utils/rbac/can";
import { Permissions } from "../../../utils/Permissions/Permissions";
import ScaleSlotsTable from "./scale-slots-table";
import styles from "./style.module.scss";

interface IProps {
  children?: React.ReactNode;
}

/** The device reads the department as two digits, so the screen shows it the
 *  same way even though it travels as a number. */
const formatDepartment = (department?: number) =>
  String(department ?? 0).padStart(2, "0");

const userName = (user?: { firstName?: string; lastName?: string } | null) =>
  user
    ? `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() || "-"
    : "-";

const ScaleSlotsComponent: React.FC<IProps> = (props) => {
  const { t } = useTranslation();
  const { direction } = useContext(AppContext);
  const navigate = useNavigate();
  const { id } = useParams();

  const { details, slots, pendingChanges, actions, loading } =
    useContext(ScaleContext);

  useEffect(() => {
    if (!id) return;

    actions.getDetails(Number(id));
    actions.getSlots(Number(id));
    actions.getPendingChanges(Number(id));
  }, [direction, id]);

  // A single unwritable row voids the whole file, and a scale with no capacity
  // has no file at all. An empty scale, on the other hand, exports fine: that
  // is how a device gets wiped.
  const hasBlockingRows = slots?.some((slot) => slot?.blocksExport) ?? false;
  const capacityMissing = (details?.slotsCount ?? 0) === 0;
  const exportDisabled = hasBlockingRows || capacityMissing;

  const pendingCount = pendingChanges?.count ?? 0;
  const awaitingConfirmation = Boolean(details?.pendingExportAt);

  return (
    <div dir={direction} className={styles.container}>
      <PageHeader
        title={t("scale_slots")}
        subTitle={
          details
            ? `${details?.name} - ${details?.store?.name ?? ""} - ${t(
                "department"
              )} ${formatDepartment(details?.department)}`
            : ""
        }
        extra={[
          <RefreshBtn
            key={0}
            loading={loading.includes("slots")}
            onClick={() => {
              actions.getSlots(Number(id));
              actions.getPendingChanges(Number(id));
            }}
          >
            {t("refresh")}
          </RefreshBtn>,
          <Can key={1} perform={Permissions.CAN_VIEW_SCALE}>
            <Divider type="vertical" />
            <Button
              icon={<HistoryOutlined />}
              onClick={() => navigate(`/scales/${id}/movements`)}
            >
              {t("scale_movements")}
            </Button>
          </Can>,
          <Can key={2} perform={Permissions.CAN_EXPORT_SCALE_FILE}>
            <Divider type="vertical" />
            <Tooltip
              title={
                capacityMissing
                  ? t("scale_capacity_not_set_hint")
                  : hasBlockingRows
                  ? t("fix_blocking_scale_rows_first")
                  : ""
              }
            >
              <span>
                <Button
                  icon={<DownloadOutlined />}
                  disabled={exportDisabled}
                  loading={loading.includes("export")}
                  onClick={() => actions.exportScale(Number(id))}
                >
                  {t("export_scale_file")}
                </Button>
              </span>
            </Tooltip>
          </Can>,
          // Downloading is not feeding. The button stays until someone says the
          // file actually reached the device, which is the only thing that
          // makes the badge honest.
          awaitingConfirmation ? (
            <Can key={3} perform={Permissions.CAN_CONFIRM_SCALE_FEEDING}>
              <Divider type="vertical" />
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                loading={loading.includes("markFed")}
                onClick={() => actions.markFed(Number(id))}
              >
                {t("mark_scale_fed")}
              </Button>
            </Can>
          ) : null,
        ]}
      />

      <AntdSpace direction="vertical" style={{ width: "100%" }} size="small">
        {capacityMissing && (
          <Alert
            type="error"
            showIcon
            message={t("scale_capacity_not_set")}
            description={t("scale_capacity_not_set_hint")}
          />
        )}

        {pendingCount > 0 && (
          <Alert
            type="warning"
            showIcon
            message={`${t("scale_pending_changes")} (${pendingCount})`}
            description={
              details?.lastExportedAt
                ? `${t("scale_last_fed_at")}: ${new Date(
                    details.lastExportedAt
                  ).toLocaleString()} - ${t("scale_last_fed_by")}: ${userName(
                    details?.lastExportedBy
                  )}`
                : t("scale_never_fed")
            }
          />
        )}

        {awaitingConfirmation && (
          <Alert
            type="info"
            showIcon
            message={t("scale_awaiting_feeding_confirmation")}
            description={`${t("scale_downloaded_at")}: ${new Date(
              details?.pendingExportAt as string
            ).toLocaleString()} - ${t("scale_downloaded_by")}: ${userName(
              details?.pendingExportBy
            )}`}
          />
        )}

        {pendingCount === 0 && !awaitingConfirmation && details && (
          <Alert
            type="success"
            showIcon
            message={t("scale_up_to_date")}
            description={
              details?.lastExportedAt
                ? `${t("scale_last_fed_at")}: ${new Date(
                    details.lastExportedAt
                  ).toLocaleString()}`
                : t("scale_never_fed")
            }
          />
        )}

        <div style={{ padding: "0 8px" }}>
          <AntdSpace size="small" wrap>
            <Tag>{`${t("slots_count")}: ${details?.slotsCount ?? 0}`}</Tag>
            <Tag color="blue">{`${t("products_count")}: ${
              details?.productsCount ?? 0
            }`}</Tag>
            <Tag>{`${t("department")}: ${formatDepartment(
              details?.department
            )}`}</Tag>
          </AntdSpace>
        </div>
      </AntdSpace>

      <ScaleSlotsTable />
    </div>
  );
};

const ScaleSlotsPage = () => {
  return (
    <ScaleContextProvider>
      <ScaleSlotsComponent />
    </ScaleContextProvider>
  );
};

export default ScaleSlotsPage;
